'use client';

import { useCallback, useEffect, useState } from 'react';
import {
    DEFAULT_WORKSPACE_IMAGE,
    probeImage
} from '@/hotdesk-ui/components/shared/SafeImage';

/**
 * Filters a workspace/listing photo list down to the URLs that actually load,
 * so a broken CDN image is skipped instead of occupying a slot. When nothing
 * survives (single broken image, or no images at all) the list collapses to
 * the default workspace photo.
 *
 * Detection is lazy by default — callers report failures through
 * `markBroken` (wired to `SafeImage`'s `onAllFailed`) as slides render. Pass
 * `probe: true` to preload every URL up front instead; use it only for a
 * single gallery, never for a grid of cards.
 *
 * Pass `toProbeUrl` with the same URL builder the gallery renders with, so the
 * probe downloads the displayed (resized) file — which the browser then reuses —
 * rather than the full-size original.
 */
export const useLoadableImages = (
    images: string[],
    {
        probe = false,
        toProbeUrl = (src: string) => src
    }: { probe?: boolean; toProbeUrl?: (src: string) => string } = {}
) => {
    const [broken, setBroken] = useState<string[]>([]);

    const markBroken = useCallback((src: string) => {
        setBroken((prev) => (prev.includes(src) ? prev : [...prev, src]));
    }, []);

    // Stable key so the probe effect re-runs only when the URLs to probe
    // change, not on every parent re-render that builds a fresh array.
    const probeUrls = images.map(toProbeUrl);
    const probeKey = probeUrls.join('|');

    useEffect(() => {
        if (!probe) return;
        let cancelled = false;
        images.forEach((src, i) => {
            probeImage(probeUrls[i]).then((ok) => {
                if (!ok && !cancelled) markBroken(src);
            });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [probeKey, probe, markBroken]);

    const loadable = images.filter((src) => !broken.includes(src));
    const isFallback = loadable.length === 0;

    return {
        images: isFallback ? [DEFAULT_WORKSPACE_IMAGE] : loadable,
        /** True when the returned list is just the default photo. */
        isFallback,
        markBroken
    };
};
